import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  MapPin, Navigation, User, Check, Clock, Car, Users,
  RefreshCw, Calendar, Plus, Trash2, Search, Filter,
  ChevronDown, ChevronRight, Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const STATUS_CONFIG = {
  on_the_way: { label: 'On The Way', icon: Navigation, color: 'bg-blue-500', dotColor: 'bg-blue-500' },
  arrived: { label: 'Arrived', icon: MapPin, color: 'bg-gold-500', dotColor: 'bg-gold-500' },
  pob: { label: 'POB', icon: User, color: 'bg-green-500', dotColor: 'bg-green-500' },
  dop: { label: 'DOP', icon: Check, color: 'bg-purple-500', dotColor: 'bg-purple-500' },
  check_in: { label: 'Checked In', icon: Clock, color: 'bg-teal-500', dotColor: 'bg-teal-500' },
  checked_in: { label: 'Checked In', icon: Clock, color: 'bg-teal-500', dotColor: 'bg-teal-500' },
  active: { label: 'Active', icon: Car, color: 'bg-green-500', dotColor: 'bg-green-500' },
};

export default function AdminLiveEventTracking() {
  const { user } = useContext(AuthContext);
  const [trackingData, setTrackingData] = useState([]);
  const [events, setEvents] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({
    driver_id: '',
    assigned_location: '',
    assigned_location_address: ''
  });
  const [expandedEvents, setExpandedEvents] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const mapRef = useRef(null);

  // Fetch live tracking data
  const fetchTrackingData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/live-event-tracking`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
    }
  }, []);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      // Get all events (not filtered by date) so admin can assign drivers to any event
      const response = await axios.get(`${API}/admin/events`);
      // Sort by start_date ascending (upcoming/nearest events first)
      const today = new Date();
      const sortedEvents = response.data
        .sort((a, b) => {
          const dateA = new Date(a.start_date);
          const dateB = new Date(b.start_date);
          // Sort upcoming events first (closest to today)
          return dateA - dateB;
        })
        // Filter to show only upcoming events (today and future)
        .filter(e => new Date(e.start_date) >= new Date(today.toDateString()))
        .slice(0, 100);
      setEvents(sortedEvents);
      console.log('Events loaded:', sortedEvents.length);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  }, []);

  // Fetch drivers
  const fetchDrivers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/drivers`);
      setDrivers(response.data);
      console.log('Drivers loaded:', response.data.length);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers');
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([fetchTrackingData(), fetchEvents(), fetchDrivers()]);
      setLoading(false);
    };
    loadInitialData();
  }, [fetchTrackingData, fetchEvents, fetchDrivers]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchTrackingData, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchTrackingData]);

  // Assign driver to event
  const handleAssignDriver = async () => {
    if (!selectedEvent || !assignForm.driver_id || !assignForm.assigned_location) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/events/${selectedEvent.id}/assign-driver`,
        {
          event_id: selectedEvent.id,
          driver_id: assignForm.driver_id,
          assigned_location: assignForm.assigned_location,
          assigned_location_address: assignForm.assigned_location_address || assignForm.assigned_location
        }
      );
      
      toast.success('Driver assigned successfully');
      setShowAssignDialog(false);
      setAssignForm({ driver_id: '', assigned_location: '', assigned_location_address: '' });
      fetchTrackingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  // Remove driver assignment
  const handleRemoveAssignment = async (eventId, assignmentId) => {
    if (!window.confirm('Remove this driver from the event?')) return;
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}/assignments/${assignmentId}`);
      toast.success('Assignment removed');
      fetchTrackingData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const toggleEventExpand = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const getDriverStatusIcon = (driver) => {
    const status = driver.latest_activity?.status || driver.assignment?.status;
    const config = STATUS_CONFIG[status];
    if (!config) return <Clock className="w-4 h-4 text-gray-400" />;
    const IconComponent = config.icon;
    return <IconComponent className="w-4 h-4" />;
  };

  // Filter events based on search query
  const filteredEvents = events.filter(event => {
    if (!eventSearchQuery.trim()) return true;
    const query = eventSearchQuery.toLowerCase();
    return (
      event.title?.toLowerCase().includes(query) ||
      event.venue?.toLowerCase().includes(query) ||
      event.category?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Live Event Tracking</h1>
          <p className="text-gray-500">Monitor driver activity in real-time</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchTrackingData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{trackingData.length}</p>
                <p className="text-sm text-gray-500">Active Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {trackingData.reduce((acc, e) => acc + (e.drivers?.length || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Active Drivers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Car className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {trackingData.reduce((acc, e) => 
                    acc + (e.drivers?.reduce((d, dr) => d + (dr.assignment?.total_trips || 0), 0) || 0), 0
                  )}
                </p>
                <p className="text-sm text-gray-500">Total Trips</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gold-100 rounded-lg">
                <Navigation className="w-5 h-5 text-gold-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {trackingData.reduce((acc, e) => 
                    acc + (e.drivers?.filter(d => d.latest_activity?.status === 'pob').length || 0), 0
                  )}
                </p>
                <p className="text-sm text-gray-500">Drivers with POB</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assign Driver Button */}
      <div className="flex justify-end">
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogTrigger asChild>
            <Button data-testid="assign-driver-btn">
              <Plus className="w-4 h-4 mr-2" />
              Assign Driver to Event
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Driver to Event</DialogTitle>
              <DialogDescription>
                Select an event and driver, then specify the pickup location
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Event</Label>
                {/* Search input for events */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search events..."
                    value={eventSearchQuery}
                    onChange={(e) => setEventSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-event-input"
                  />
                </div>
                <Select 
                  value={selectedEvent?.id || ''} 
                  onValueChange={(v) => setSelectedEvent(events.find(e => e.id === v))}
                >
                  <SelectTrigger data-testid="select-event">
                    <SelectValue placeholder="Select an event" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {filteredEvents.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500 text-center">No events found</div>
                    ) : (
                      filteredEvents.slice(0, 50).map(event => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.title} - {new Date(event.start_date).toLocaleDateString()}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {filteredEvents.length > 50 && (
                  <p className="text-xs text-gray-400 mt-1">Showing first 50 results. Use search to find more.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Driver</Label>
                <Select 
                  value={assignForm.driver_id} 
                  onValueChange={(v) => setAssignForm(f => ({ ...f, driver_id: v }))}
                >
                  <SelectTrigger data-testid="select-driver">
                    <SelectValue placeholder="Select a driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name || driver.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pickup Location Name</Label>
                <Input
                  placeholder="e.g., Hilton Hotel, Main Entrance"
                  value={assignForm.assigned_location}
                  onChange={(e) => setAssignForm(f => ({ ...f, assigned_location: e.target.value }))}
                  data-testid="input-location-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Address</Label>
                <Input
                  placeholder="e.g., 123 Main St, Miami, FL 33101"
                  value={assignForm.assigned_location_address}
                  onChange={(e) => setAssignForm(f => ({ ...f, assigned_location_address: e.target.value }))}
                  data-testid="input-location-address"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignDriver} data-testid="confirm-assign-btn">
                Assign Driver
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Events with Drivers */}
      {trackingData.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No Active Events</h3>
            <p className="text-gray-400 mt-1">No drivers are currently checked in to any events.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {trackingData.map((eventData) => (
            <Card key={eventData.event?.id} className="overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => toggleEventExpand(eventData.event?.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedEvents[eventData.event?.id] ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <CardTitle className="text-lg">{eventData.event?.title}</CardTitle>
                      <CardDescription>
                        {eventData.event?.venue} • {new Date(eventData.event?.start_date).toLocaleDateString()}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{eventData.drivers?.length || 0}</p>
                      <p className="text-xs text-gray-500">Drivers</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {eventData.drivers?.reduce((acc, d) => acc + (d.assignment?.total_trips || 0), 0)}
                      </p>
                      <p className="text-xs text-gray-500">Trips</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              {expandedEvents[eventData.event?.id] && (
                <CardContent className="border-t">
                  <div className="space-y-3 pt-4">
                    {eventData.drivers?.map((driver) => {
                      const status = driver.latest_activity?.status || driver.assignment?.status;
                      const statusConfig = STATUS_CONFIG[status] || { label: status, dotColor: 'bg-gray-400' };
                      
                      return (
                        <div 
                          key={driver.assignment?.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${statusConfig.dotColor} animate-pulse`} />
                            <div>
                              <p className="font-medium">{driver.assignment?.driver_name}</p>
                              <p className="text-sm text-gray-500">{driver.assignment?.assigned_location}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-medium capitalize">{statusConfig.label}</p>
                              <p className="text-xs text-gray-500">
                                {driver.assignment?.total_trips || 0} trips • {' '}
                                <span className="text-blue-600 font-medium">
                                  {driver.assignment?.total_driving_minutes 
                                    ? `${(driver.assignment.total_driving_minutes / 60).toFixed(1)}h`
                                    : '0h'}
                                </span>
                              </p>
                            </div>
                            {driver.latest_activity?.timestamp && (
                              <div className="text-xs text-gray-400">
                                {new Date(driver.latest_activity.timestamp).toLocaleTimeString()}
                              </div>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAssignment(eventData.event?.id, driver.assignment?.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {(!eventData.drivers || eventData.drivers.length === 0) && (
                      <p className="text-center text-gray-400 py-4">No drivers assigned to this event yet</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* All Events Section - For assigning drivers */}
      <div className="mt-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <h2 className="text-xl font-bold">Upcoming Events</h2>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search events..."
              value={eventSearchQuery}
              onChange={(e) => setEventSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-events"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvents.slice(0, 12).map((event) => (
            <Card key={event.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <h3 className="font-semibold">{event.title}</h3>
                <p className="text-sm text-gray-500">{event.venue}</p>
                <p className="text-sm text-gray-400">
                  {new Date(event.start_date).toLocaleDateString()}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowAssignDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Assign Driver
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        {filteredEvents.length === 0 && (
          <p className="text-center text-gray-500 py-8">No events found matching "{eventSearchQuery}"</p>
        )}
        {filteredEvents.length > 12 && (
          <p className="text-center text-gray-400 mt-4">
            Showing 12 of {filteredEvents.length} events. Use search to find specific events.
          </p>
        )}
      </div>
    </div>
  );
}
